import React, { useState, useEffect } from "react";
import { fetchWithAuth } from "../../../../api-client/APIClient";
import { useForm } from "react-hook-form";
import { SkeletonLoader } from "@merge-api/merge-javascript-shared";
import { User, Comment } from "../../../../models/Entities";
import { Form, Col, InputGroup } from "react-bootstrap";
import { fetchIntegrationIssueComments } from "../../../pages/utils/IntegrationsManagementAPIClient";
import { getCapitalizedTimeFromNow } from "../../../shared/utils/SharedComponentUtils";
import { UserAvatar, UserAvatarSize } from "../../../shared/MergeAvatars";
import { Button, ButtonVariant, Text } from "@merge-api/merge-javascript-shared";

const commentStyle = {
  overflow: "hidden",
  overflowWrap: "break-word",
  height: "40px",
};

interface CommentsCardProps {
  integrationID?: string;
  integrationIssueID?: string;
}

export function IntegrationIssueComments({ integrationID, integrationIssueID }: CommentsCardProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { register, handleSubmit, reset } = useForm();
  const [comments, setComments] = useState<undefined | Comment[]>();

  useEffect(() => {
    fetchComments();
  }, []);

  function fetchComments() {
    if (integrationID) {
      fetchWithAuth({
        path: `/integrations/${integrationID}/comments`,
        method: "GET",
        onResponse: (data) => {
          setComments(data.results);
        },
      });
    }
    if (integrationIssueID) {
      fetchIntegrationIssueComments({
        integrationIssueID,
        onSuccess: (data: { results: Comment[] }) => {
          setComments(data.results);
        },
      });
    }
  }

  const onSubmit = (data: { body: any }) => {
    setIsLoading(true);

    if (integrationID) {
      fetchWithAuth({
        path: `/integrations/${integrationID}/comments`,
        method: "POST",
        body: { body: data.body },
        onResponse: (data) => {
          comments && comments.unshift(data);
          reset();
          fetchComments();
          setIsLoading(false);
        },
      });
    }
    if (integrationIssueID) {
      fetchWithAuth({
        path: `/integrations/issues/${integrationIssueID}/comments`,
        method: "POST",
        body: { body: data.body },
        onResponse: (data) => {
          comments && comments.unshift(data);
          reset();
          fetchComments();
          setIsLoading(false);
        },
      });
    }
  };

  return (
    <div className="-m-6">
      <div className="px-4 py-3 border-b border-gray-10">
        <Form
          className="form-inline w-100 mt-2"
          onSubmit={handleSubmit(onSubmit)}
          autoComplete="off"
        >
          <Form.Group className="w-100">
            <Col>
              <InputGroup className="input-group input-group-flush">
                <input
                  className="form-control form-control-flush w-100"
                  data-toggle="autosize"
                  placeholder="Leave a comment..."
                  name="body"
                  type="text"
                  style={commentStyle as any}
                  ref={register({
                    required: true,
                    minLength: 1,
                  })}
                />
              </InputGroup>
            </Col>
            <Col className="col-auto align-self-end">
              <Button
                size="sm"
                variant={ButtonVariant.TertiaryWhite}
                loading={isLoading}
                className="mb-2"
                type="submit"
              >
                Submit
              </Button>
            </Col>
          </Form.Group>
        </Form>
      </div>
      <div className="px-4 py-3 max-h-96 overflow-y-auto">
        {comments ? (
          comments.map((comment) => <CommentRow comment={comment} key={comment.id} />)
        ) : (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex flex-row gap-x-3 w-full">
                <SkeletonLoader width={32} height={32} borderRadius={999} />
                <div className="flex flex-col gap-2 flex-1">
                  <SkeletonLoader width={120} height={16} borderRadius={4} />
                  <SkeletonLoader width={200} height={16} borderRadius={4} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface CommentRowProps {
  comment: {
    id: string;
    created_at: string;
    body: string;
    user: User;
  };
}

export function CommentRow(props: CommentRowProps) {
  return (
    <div className="flex flex-row gap-x-3 w-full mb-3" key={props.comment.id}>
      <div className="flex flex-shrink-0">
        <UserAvatar user={props.comment.user} size={UserAvatarSize.sm} />
      </div>
      <div className="inline-block py-4 px-5 bg-gray-0 rounded-lg">
        <div className="flex flex-row items-center justify-between gap-x-5 mb-1.5">
          <Text variant="h6">{props.comment.user.name}</Text>
          <Text variant="sm" className="text-gray-40">
            {getCapitalizedTimeFromNow(props.comment.created_at)}
          </Text>
        </div>
        <Text variant="sm" className="text-break">
          {props.comment.body}
        </Text>
      </div>
    </div>
  );
}
